import { FormikValues } from 'formik';

import {
  getPublicPharmacyRatingItems,
  getPublicProfessionalRatingItems,
  publicPharmacyRatingItems,
  publicProfessionalRatingItems,
  publicRatePharmacy,
  publicRateProfessional,
  PublicRatingType,
  utilActions,
} from '@pharmaplan/common';

import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import strings from '../../localization';

const { professional, pharmacy } = PublicRatingType;

const usePublicRating = (
  ratingType: PublicRatingType,
  ratingLevel: { [key: string]: number },
  bookingId: string,
  pharmacistId: string,
  pharmacyId: string,
) => {
  const dispatch = useAppDispatch();
  const professionalRatingItems = useAppSelector(publicProfessionalRatingItems);
  const pharmacyRatingItems = useAppSelector(publicPharmacyRatingItems);

  const getAPI = {
    [pharmacy]: getPublicPharmacyRatingItems,
    [professional]: getPublicProfessionalRatingItems,
  };

  const submitAPI = {
    [pharmacy]: publicRatePharmacy,
    [professional]: publicRateProfessional,
  };

  const ratingItems = {
    [professional]: professionalRatingItems.professionalRatingItems,
    [pharmacy]: pharmacyRatingItems.pharmacyRatingItems,
  };

  const ratingItemId = {
    [professional]: 'professionalRatingItemId',
    [pharmacy]: 'pharmacyRatingItemId',
  };

  const name = {
    [professional]: professionalRatingItems.professionalName,
    [pharmacy]: pharmacyRatingItems.pharmacyName,
  };

  const title = {
    [professional]: strings.formatString(
      strings.myUserRating,
      strings.professional,
    ),
    [pharmacy]: strings.formatString(strings.myUserRating, strings.pharmacy),
  };

  const questions = {
    [pharmacy]: pharmacyRatingItems?.pharmacyRatingQuestions,
    [professional]: professionalRatingItems?.professionalRatingQuestions,
  };

  const questionsIdProperty = {
    [professional]: 'professionalRatingQuestionId',
    [pharmacy]: 'pharmacyRatingQuestionId',
  };

  const pharmacyHandleSubmit = (values: FormikValues) => {
    const levels = pharmacyRatingItems?.pharmacyRatingItems?.map((item) => {
      const { pharmacyRatingItemId } = item ?? {};
      return {
        pharmacyRatingItemId,
        level: ratingLevel[pharmacyRatingItemId] ?? 0,
      };
    });

    const answers = pharmacyRatingItems?.pharmacyRatingQuestions?.map(
      (item) => {
        const { pharmacyRatingQuestionId } = item ?? {};
        return {
          pharmacyRatingQuestionId,
          answer: values?.[pharmacyRatingQuestionId] ?? 0,
        };
      },
    );

    const { comment } = values ?? {};

    dispatch(
      publicRatePharmacy({
        bookingId: bookingId ?? '',
        levels: [...levels],
        favorite: values?.favourite,
        comment,
        professionalId: pharmacistId,
        pharmacyId: pharmacyId ?? '',
        answers: answers ?? [],
      }),
    );
  };

  const professionalHandleSubmit = (values: FormikValues) => {
    const levels = professionalRatingItems?.professionalRatingItems?.map(
      (item) => {
        const { professionalRatingItemId } = item ?? {};
        return {
          professionalRatingItemId,
          level: ratingLevel[professionalRatingItemId] ?? 0,
        };
      },
    );

    const answers = professionalRatingItems?.professionalRatingQuestions?.map(
      (item) => {
        const { professionalRatingQuestionId } = item ?? {};
        return {
          professionalRatingQuestionId,
          answer: values?.[professionalRatingQuestionId] ?? 0,
        };
      },
    );

    const { comment } = values ?? {};

    dispatch(
      publicRateProfessional({
        bookingId: bookingId ?? '',
        levels: professionalRatingItems?.displayDetailForm ? [...levels] : [],
        favorite: values?.favourite,
        comment,
        professionalId: pharmacistId,
        pharmacyId,
        answers,
      }),
    );
  };

  const submitConfig = {
    [pharmacy]: pharmacyHandleSubmit,
    [professional]: professionalHandleSubmit,
  };

  const maxRatedItems = {
    [pharmacy]: pharmacyRatingItems?.pharmacyRatingItems?.length,
    [professional]: professionalRatingItems?.professionalRatingItems?.length,
  };

  const successActions = {
    [pharmacy]: utilActions.publicRatePharmacy,
    [professional]: utilActions.publicRateProfessional,
  };

  const displayDetailFormConfig = {
    [pharmacy]: true,
    [professional]: professionalRatingItems?.displayDetailForm,
  };

  return {
    getAPI: getAPI[ratingType],
    submitAPI: submitAPI[ratingType],
    ratingItems: ratingItems[ratingType],
    ratingItemId: ratingItemId[ratingType],
    name: name[ratingType],
    title: title[ratingType],
    questions: questions[ratingType],
    questionsIdProperty: questionsIdProperty[ratingType],
    handleSubmit: submitConfig[ratingType],
    maxRatedItemsPossible: maxRatedItems[ratingType],
    displayDetailForm: displayDetailFormConfig[ratingType],
    successAction: successActions[ratingType],
  };
};

export default usePublicRating;
